import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Injector, input, signal } from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { NeoRadioModule } from '@neogov/pattern-library/neo-radio';
import { FieldErrorsComponent, shouldSurfaceFieldError } from '../field-errors';

export interface RadioOption {
  value: string;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'cm-radio-group',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NeoRadioModule,
    FieldErrorsComponent,
  ],
  templateUrl: './radio-group.component.html',
  styleUrl: './radio-group.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: RadioGroupComponent,
      multi: true,
    },
  ],
})
export class RadioGroupComponent implements ControlValueAccessor {
  private readonly _cdr = inject(ChangeDetectorRef);

  public readonly label = input.required<string>();
  public readonly autId = input.required<string>();
  public readonly options = input<RadioOption[]>([]);
  public readonly hint = input<string>('');
  public readonly required = input<boolean>(false);

  // Error handling inputs
  public readonly errorMessages = input<Partial<Record<string, string>>>({});
  public readonly showErrorsWhen = input<'touched' | 'dirty' | 'always'>('touched');
  public readonly hideErrors = input<boolean>(false);

  public value: string | null = null;
  public disabled = false;

  /** Stable id for the group label, used to wire `aria-labelledby` on the radio group. */
  public get labelId(): string {
    return `${this.autId()}-label`;
  }

  /** Stable id for the error container, referenced by `aria-describedby` when an error shows. */
  public get errorId(): string {
    return `${this.autId()}-error`;
  }

  /**
   * Whether the field's validation error is currently surfaced. Read as a
   * template method (not a computed) so it re-evaluates on each change-detection
   * pass and stays in sync with the outer control's touched/invalid state —
   * this drives aria-invalid and the aria-describedby link (WCAG 3.3.1 / 4.1.2).
   */
  public isErrorVisible(): boolean {
    return shouldSurfaceFieldError(this.ngControl()?.control, this.showErrorsWhen(), this.hideErrors());
  }

  private readonly _injector = inject(Injector);
  public readonly ngControl = signal<NgControl | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onChange: (value: string | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched: () => void = () => {};

  constructor() {
    // Defer NgControl lookup to avoid circular dependency
    queueMicrotask(() => {
      try {
        const ngControl =
          this._injector.get(NgControl, null, { optional: true, self: true }) || null;
        this.ngControl.set(ngControl);
      } catch {
        this.ngControl.set(null);
      }
    });
  }

  public writeValue(value: string | null): void {
    this.value = value;
    this._cdr.markForCheck();
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._cdr.markForCheck();
  }

  public onRadioChange(value: string): void {
    this.value = value;
    this.onChange(this.value);
  }

  public onBlur(): void {
    this.onTouched();
  }
}
